import React, { useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  Callout,
  Card,
  DataList,
  DataListItem,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Flex,
  Heading,
  Label,
  Progress,
  Text,
} from '@/components/ui';
import { IconAlertTriangle, IconCloudUpload } from '@tabler/icons-react';
import {
  FileProgressItem,
  FileProgressList,
} from '@/components/file-progress-list';
import { CsvUploadConfirmationModalProps, FileUploadStatus } from './types';
import { formatFileSize } from '@/utils/fileUtils.ts';

const formatFileCount = (count: number): string => {
  return `${count} ${count === 1 ? 'file' : 'files'}`;
};

const mapUploadStatusToOperationStatus = (
  status: FileUploadStatus
): FileProgressItem['status'] => {
  switch (status) {
    case 'uploading':
      return 'processing';
    case 'completed':
      return 'completed';
    case 'cancelled':
      return 'cancelled';
    case 'error':
      return 'error';
    case 'pending':
    default:
      return 'pending';
  }
};

export const CsvUploadConfirmationModal: React.FC<
  CsvUploadConfirmationModalProps
> = ({
  isOpen,
  onClose,
  onConfirm,
  onCancelUpload,
  onRemoveFile,
  files,
  isUploading = false,
  fileProgress = [],
  currentUploadingFileIndex,
}) => {
  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  const totalFiles = files.length;

  // Convert files and progress to FileProgressItem format
  const fileProgressItems: FileProgressItem[] = useMemo(() => {
    return files.map((file, index) => {
      // Try to find progress by index first
      const progressData =
        fileProgress[index] ||
        fileProgress.find(
          fp => fp.file.name === file.name && fp.file.size === file.size
        );

      return {
        id: index,
        name: file.name,
        size: file.size,
        status: progressData
          ? mapUploadStatusToOperationStatus(progressData.status)
          : isUploading
            ? 'pending'
            : 'pending',
        progress: progressData?.progress || 0,
        error: progressData?.error,
      };
    });
  }, [files, fileProgress, isUploading]);

  // Calculate overall progress
  const overallProgress =
    fileProgressItems.length > 0
      ? Math.round(
          fileProgressItems.reduce((sum, fp) => sum + fp.progress, 0) /
            fileProgressItems.length
        )
      : 0;

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open: boolean) => !open && !isUploading && onClose()}
    >
      <Dialog.Content className="sm:max-w-[500px]">
        <DialogHeader
          startSlot={
            <Avatar radius="medium" size="medium">
              <IconCloudUpload style={{ color: 'var(--blue-9)' }} />
            </Avatar>
          }
          title="Confirm Upload"
          subtitle=" Confirm upload of selected CSV files"
        />
        <DialogBody>
          <Box mt="4">
            <Flex direction="column" gap="3">
              {/* Upload Summary */}
              <Card variant="classic" size="small" background={'var(--gray-2)'}>
                <Heading size="2xsmall" weight="medium" mb="2">
                  Upload Summary
                </Heading>
                <DataList
                  size="medium"
                  orientation="horizontal"
                  alignValues="end"
                >
                  <DataListItem
                    label="Files to upload:"
                    value={formatFileCount(totalFiles)}
                  />

                  <DataListItem
                    label="Total size:"
                    value={formatFileSize(totalSize)}
                  />
                </DataList>
                {isUploading && fileProgressItems.length > 0 && (
                  <Card variant="classic" size="xsmall" color="info" mt="4">
                    <Flex
                      direction="column"
                      gap="2"
                      style={{ gridColumn: '1/-1' }}
                    >
                      <Flex justify="between" align="center">
                        <Text size="3xsmall">
                          Overall Progress
                          <Text as="span" size="inherit" weight="medium" ml="2">
                            {overallProgress}%
                          </Text>
                        </Text>
                        <Text size="3xsmall">
                          {`${fileProgressItems.filter(fp => fp.status === 'completed').length} of ${files.length} files uploaded`}
                        </Text>
                      </Flex>
                      <Progress size="small" value={overallProgress} />
                    </Flex>
                  </Card>
                )}
              </Card>

              {/* File List */}
              <Box mt="4">
                <Flex justify="between" align="center">
                  <Label>Selected Files:</Label>
                  {isUploading && fileProgressItems.length > 0 && (
                    <Text size="2xsmall" color="gray">
                      {overallProgress}% complete
                    </Text>
                  )}
                </Flex>
                <FileProgressList
                  files={fileProgressItems}
                  currentProcessingIndex={currentUploadingFileIndex}
                  isProcessing={isUploading}
                  onRemoveFile={
                    !isUploading && onRemoveFile
                      ? (id) => {
                          if (typeof id === 'number') onRemoveFile(id);
                        }
                      : undefined
                  }
                />
              </Box>

              {/* Warning about potential overwrites */}
              <Callout
                icon={<IconAlertTriangle />}
                title="Important Note"
                variant="surface"
              >
                If files with the same names already exist, new versions will be
                created automatically. This action cannot be undone.
              </Callout>

              {!isUploading && (
                <Text size="2xsmall">
                  Files will be uploaded immediately after confirmation. You'll
                  receive a notification when the upload is complete.
                </Text>
              )}
            </Flex>
          </Box>
        </DialogBody>

        <DialogFooter>
          <Button
            variant="outlined"
            color="secondary"
            onClick={isUploading ? onCancelUpload : onClose}
          >
            {isUploading ? 'Cancel Upload' : 'Cancel'}
          </Button>
          <Button
            variant="classic"
            color="primary"
            onClick={onConfirm}
            startIcon={<IconCloudUpload />}
            disabled={isUploading}
            loading={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload Files'}
          </Button>
        </DialogFooter>
      </Dialog.Content>
    </Dialog.Root>
  );
};
